import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Trash2 } from 'lucide-react'
import { Button, Input, Select, Checkbox, Textarea, InfoBanner, HelpIcon } from '@/components/ui'
import {
  PrivacySubject,
  PrivacyJurisdiction,
  VerificationStatus,
  PrivacyStatus,
  JURISDICTION_LABELS,
  STATUS_LABELS,
  VERIFICATION_STATUS_LABELS,
} from '@/models/privacy'
import { toDateInputValue } from '@/lib/utils/date'
import { PRIVACY_HELP } from '../constants/help-content'

// Zod schemas
const createPrivacySchema = z.object({
  email: z.string().optional(),
  phone: z.string().optional(),
  first_name: z.string().optional(),
  last_name: z.string().optional(),
  opt_out_sale: z.boolean(),
  opt_out_share: z.boolean(),
  remove_data: z.boolean(),
  do_not_contact: z.boolean(),
  jurisdiction: z.enum(['ccpa', 'gdpr', 'other'] as const),
  received_at: z.string().optional(),
  source: z.string().optional(),
  notes: z.string().optional(),
}).superRefine((data, ctx) => {
  const hasEmail = data.email && data.email.trim().length > 0
  const hasPhone = data.phone && data.phone.trim().length > 0

  // Require at least one identifier
  if (!hasEmail && !hasPhone) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Either email or phone is required',
      path: ['email'],
    })
  }

  // Validate email format if provided
  if (hasEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email!)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Invalid email address',
      path: ['email'],
    })
  }
})

const updatePrivacySchema = z.object({
  phone: z.string().optional(),
  first_name: z.string().optional(),
  last_name: z.string().optional(),
  opt_out_sale: z.boolean().optional(),
  opt_out_share: z.boolean().optional(),
  remove_data: z.boolean().optional(),
  do_not_contact: z.boolean().optional(),
  verification_status: z.enum(['pending', 'verified', 'failed'] as const).optional(),
  status: z.enum(['received', 'in_progress', 'completed', 'denied'] as const).optional(),
  notes: z.string().optional(),
})

export type CreatePrivacyFormData = z.infer<typeof createPrivacySchema>
export type UpdatePrivacyFormData = z.infer<typeof updatePrivacySchema>

// Select options
const jurisdictionOptions = (Object.keys(JURISDICTION_LABELS) as PrivacyJurisdiction[]).map((key) => ({
  value: key,
  label: JURISDICTION_LABELS[key],
}))

const statusOptions = (Object.keys(STATUS_LABELS) as PrivacyStatus[]).map((key) => ({
  value: key,
  label: STATUS_LABELS[key],
}))

const verificationStatusOptions = (Object.keys(VERIFICATION_STATUS_LABELS) as VerificationStatus[]).map((key) => ({
  value: key,
  label: VERIFICATION_STATUS_LABELS[key],
}))

interface CreateFormProps {
  mode: 'create'
  initialData?: never
  onSubmit: (data: CreatePrivacyFormData) => void
  onCancel: () => void
  isSubmitting?: boolean
}

interface EditFormProps {
  mode: 'edit'
  initialData: PrivacySubject
  onSubmit: (data: UpdatePrivacyFormData) => void
  onCancel: () => void
  onDelete?: () => void
  isSubmitting?: boolean
}

type PrivacyFormProps = CreateFormProps | EditFormProps

export function PrivacyForm(props: PrivacyFormProps) {
  const { mode, onCancel, isSubmitting = false } = props
  const isEdit = mode === 'edit'

  // Create form
  const createForm = useForm<CreatePrivacyFormData>({
    resolver: zodResolver(createPrivacySchema),
    defaultValues: {
      email: '',
      phone: '',
      first_name: '',
      last_name: '',
      opt_out_sale: false,
      opt_out_share: false,
      remove_data: false,
      do_not_contact: false,
      jurisdiction: 'ccpa',
      received_at: toDateInputValue(new Date().toISOString()),
      source: '',
      notes: '',
    },
  })

  // Edit form
  const editForm = useForm<UpdatePrivacyFormData>({
    resolver: zodResolver(updatePrivacySchema),
    defaultValues: isEdit
      ? {
          phone: props.initialData?.phone || '',
          first_name: props.initialData?.first_name || '',
          last_name: props.initialData?.last_name || '',
          opt_out_sale: props.initialData?.opt_out_sale || false,
          opt_out_share: props.initialData?.opt_out_share || false,
          remove_data: props.initialData?.remove_data || false,
          do_not_contact: props.initialData?.do_not_contact || false,
          verification_status: props.initialData?.verification_status || 'pending',
          status: props.initialData?.status || 'received',
          notes: props.initialData?.notes || '',
        }
      : {},
  })

  const handleCreateSubmit = (data: CreatePrivacyFormData) => {
    if (mode === 'create') {
      props.onSubmit(data)
    }
  }

  const handleEditSubmit = (data: UpdatePrivacyFormData) => {
    if (mode === 'edit') {
      props.onSubmit(data)
    }
  }

  if (isEdit) {
    const { register, handleSubmit, formState: { errors } } = editForm
    const initialData = props.initialData

    return (
      <form onSubmit={handleSubmit(handleEditSubmit)} className="flex flex-col">
        {/* Scrollable Content */}
        <div className="space-y-6 overflow-y-auto max-h-[50vh] px-1">
        {/* Contact Information */}
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-slate-900 dark:text-white">Contact Information</h3>

          {initialData.email ? (
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Email
              </label>
              <Input type="email" value={initialData.email} disabled className="bg-slate-50 dark:bg-slate-900" />
              <p className="text-xs text-slate-500 mt-1">Email cannot be changed</p>
            </div>
          ) : (
            <div className="p-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-600 dark:text-slate-400">
              Phone-only record (no email on file)
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                First Name
              </label>
              <Input {...register('first_name')} placeholder="First name" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Last Name
              </label>
              <Input {...register('last_name')} placeholder="Last name" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Phone {!initialData.email && <span className="text-slate-500">(Primary identifier)</span>}
            </label>
            <Input
              {...register('phone')}
              placeholder="+1 (555) 123-4567"
              disabled={!initialData.email}
              className={!initialData.email ? "bg-slate-50 dark:bg-slate-900" : ""}
            />
            {!initialData.email && (
              <p className="text-xs text-slate-500 mt-1">Phone cannot be changed for phone-only records</p>
            )}
          </div>
        </div>

        {/* Request Details */}
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-slate-900 dark:text-white">Request Details</h3>

          <InfoBanner variant="tip">
            Status and verification can also be updated via API: PATCH /v1/privacy/{'{id}'}
          </InfoBanner>

          <div>
            <div className="flex items-center gap-1 mb-1">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                Status
              </label>
              <HelpIcon content={PRIVACY_HELP.status.workflow} size="sm" position="right" />
            </div>
            <Select
              options={statusOptions}
              {...register('status')}
              error={errors.status?.message}
            />
          </div>

          <div>
            <div className="flex items-center gap-1 mb-1">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                Verification Status
              </label>
              <HelpIcon content={PRIVACY_HELP.verification.howTo} size="sm" position="right" />
            </div>
            <Select
              options={verificationStatusOptions}
              {...register('verification_status')}
              error={errors.verification_status?.message}
            />
          </div>
        </div>

        {/* Request Types */}
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-slate-900 dark:text-white">Request Types</h3>
          <div className="space-y-3">
            <Checkbox {...register('opt_out_sale')} label="Opt-Out of Sale" />
            <Checkbox {...register('opt_out_share')} label="Opt-Out of Sharing" />
            <Checkbox {...register('remove_data')} label="Remove/Delete Data" />
            <Checkbox {...register('do_not_contact')} label="Do Not Contact" />
          </div>
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            Notes
          </label>
          <Textarea {...register('notes')} placeholder="Additional notes..." rows={3} />
        </div>
        </div>

        {/* Actions - Fixed Footer */}
        <div className="flex items-center justify-between pt-4 mt-4 border-t border-slate-200 dark:border-slate-700 flex-shrink-0">
          <div>
            {props.onDelete && (
              <Button
                type="button"
                variant="secondary"
                onClick={props.onDelete}
                disabled={isSubmitting}
                className="text-red-600 dark:text-red-400 border-red-300 dark:border-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </Button>
            )}
          </div>
          <div className="flex gap-3">
            <Button type="button" variant="secondary" onClick={onCancel} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Update Subject'}
            </Button>
          </div>
        </div>
      </form>
    )
  }

  // Create form
  const { register, handleSubmit, formState: { errors }, watch } = createForm
  const watchedEmail = watch('email')
  const watchedPhone = watch('phone')
  const hasEmail = watchedEmail && watchedEmail.trim().length > 0
  const hasPhone = watchedPhone && watchedPhone.trim().length > 0
  const isEitherOrError = errors.email?.message === 'Either email or phone is required'

  return (
    <form onSubmit={handleSubmit(handleCreateSubmit)} className="flex flex-col">
      {/* Scrollable Content */}
      <div className="space-y-6 overflow-y-auto max-h-[50vh] px-1">
      {/* Contact Information */}
      <div className="space-y-4">
        <h3 className="text-sm font-medium text-slate-900 dark:text-white">Contact Information</h3>

        {/* Info banner */}
        <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg text-sm text-blue-700 dark:text-blue-300">
          At least one contact method (email or phone) is required.
        </div>

        {/* Either/or error message */}
        {isEitherOrError && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-600 dark:text-red-400">
            Please provide either an email address or a phone number.
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            Email {!hasPhone && <span className="text-red-500">*</span>}
          </label>
          <Input
            type="email"
            {...register('email')}
            placeholder="email@example.com"
            error={!isEitherOrError ? errors.email?.message : undefined}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              First Name
            </label>
            <Input {...register('first_name')} placeholder="First name" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Last Name
            </label>
            <Input {...register('last_name')} placeholder="Last name" />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            Phone {!hasEmail && <span className="text-red-500">*</span>}
          </label>
          <Input {...register('phone')} placeholder="+1 (555) 123-4567" />
        </div>
      </div>

      {/* Request Details */}
      <div className="space-y-4">
        <h3 className="text-sm font-medium text-slate-900 dark:text-white">Request Details</h3>

        <div>
          <div className="flex items-center gap-1 mb-1">
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
              Jurisdiction <span className="text-red-500">*</span>
            </label>
            <HelpIcon content={PRIVACY_HELP.dueDate.general} size="sm" position="right" />
          </div>
          <Select
            options={jurisdictionOptions}
            {...register('jurisdiction')}
            error={errors.jurisdiction?.message}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            Date Received
          </label>
          <Input type="date" {...register('received_at')} />
        </div>

        <InfoBanner variant="info">
          Due date is auto-calculated based on jurisdiction and received date.
        </InfoBanner>

        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            Source
          </label>
          <Input
            {...register('source')}
            placeholder="e.g., Web form, Email, Phone"
          />
        </div>
      </div>

      {/* Request Types */}
      <div className="space-y-4">
        <div className="flex items-center gap-1">
          <h3 className="text-sm font-medium text-slate-900 dark:text-white">Request Types</h3>
          <HelpIcon content={PRIVACY_HELP.requestTypes.general} size="sm" position="right" />
        </div>
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Checkbox {...register('opt_out_sale')} label="Opt-Out of Sale" />
            <HelpIcon content={PRIVACY_HELP.requestTypes.opt_out_sale} size="sm" position="right" />
          </div>
          <div className="flex items-center gap-2">
            <Checkbox {...register('opt_out_share')} label="Opt-Out of Sharing" />
            <HelpIcon content={PRIVACY_HELP.requestTypes.opt_out_share} size="sm" position="right" />
          </div>
          <div className="flex items-center gap-2">
            <Checkbox {...register('remove_data')} label="Remove/Delete Data" />
            <HelpIcon content={PRIVACY_HELP.requestTypes.remove_data} size="sm" position="right" />
          </div>
          <div className="flex items-center gap-2">
            <Checkbox {...register('do_not_contact')} label="Do Not Contact" />
            <HelpIcon content={PRIVACY_HELP.requestTypes.do_not_contact} size="sm" position="right" />
          </div>
        </div>
      </div>

      {/* Notes */}
      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          Notes
        </label>
        <Textarea {...register('notes')} placeholder="Additional notes..." rows={3} />
      </div>
      </div>

      {/* Actions - Fixed Footer */}
      <div className="flex items-center justify-end gap-3 pt-4 mt-4 border-t border-slate-200 dark:border-slate-700 flex-shrink-0">
        <Button type="button" variant="secondary" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" disabled={isSubmitting}>
          {isSubmitting ? 'Saving...' : 'Create Subject'}
        </Button>
      </div>
    </form>
  )
}
